import { create } from "zustand";
import { useShallow } from "zustand/react/shallow";
import { pushToast } from "@/hooks/useToasts";
import { MockAdapter } from "@/data/mockAdapter";
import { TbAdapter, type AvailableSite } from "@/data/tbAdapter";
import { EmbeddedCtxSource } from "@/data/tb/embedded";
import { NoSiteAssetsError } from "@/data/tb/topologyFromTb";
import { TOPOLOGIES, topologyById } from "@/data/topologies";
import type { EmsAdapter, AdapterMode } from "@/data/adapter";
import type { SiteSnapshot } from "@/data/types";
import type { EntityRef } from "@/data/tb/types";

/**
 * Retention window for the live spark chart: 60 minutes.
 *
 * The edge EMS uplinks every ~2 minutes (cloud-connector batching), so
 * the smallest meaningful inspection window is 60 minutes (~30 samples).
 * Anything shorter would either be near-empty or look like a flat line
 * waiting for the next uplink.
 *
 * Mock mode still ticks at 1 Hz, so we also cap the array to avoid
 * runaway memory — 1800 entries = 30 minutes at 1 Hz, plenty for dev.
 */
const RECENT_WINDOW_MS = 60 * 60_000;
const RECENT_HARD_CAP = 1800;

function pruneRecent(recent: SiteSnapshot[], latest: SiteSnapshot): SiteSnapshot[] {
  const cutoff = latest.ts - RECENT_WINDOW_MS;
  const next = recent.filter((s) => s.ts >= cutoff);
  // Live snapshots are stamped from the newest telemetry SOURCE ts
  // (DATA-05), so several flushes between ~2-min uplinks can carry the
  // SAME ts (attribute polls, heartbeat-age ticks). Replace instead of
  // append so charts never see duplicate x-points; skip the rare
  // strictly-older emit (a REST backfill point can briefly outrun the
  // WS watermark) so the buffer stays ts-sorted.
  if (next.length) {
    const lastTs = next[next.length - 1].ts;
    if (latest.ts === lastTs) {
      next[next.length - 1] = latest;
      return next;
    }
    if (latest.ts < lastTs) return next;
  }
  next.push(latest);
  return next.length > RECENT_HARD_CAP ? next.slice(-RECENT_HARD_CAP) : next;
}

/**
 * Merge a backfill batch (historical snapshots from `adapter.recentHistory()`)
 * with the currently-accumulating live recent[] buffer. The backfill points
 * sit BEFORE the live point in time, so we union by ts and sort.
 */
function mergeBackfill(live: SiteSnapshot[], backfill: SiteSnapshot[]): SiteSnapshot[] {
  if (!backfill.length) return live;
  const seen = new Set<number>(live.map((s) => s.ts));
  const combined = [...live];
  for (const s of backfill) {
    if (!seen.has(s.ts)) combined.push(s);
  }
  combined.sort((a, b) => a.ts - b.ts);
  return combined;
}

interface SiteStoreState {
  mode: AdapterMode;
  topologyId: string;
  adapter: EmsAdapter;
  snapshot: SiteSnapshot;
  /** Sliding window of the last 60 minutes of snapshots, pruned by ts. */
  recent: SiteSnapshot[];
  operator: string;
  /**
   * "connecting" while the embedded adapter swap is in flight; "live"
   * once TB telemetry is flowing; "reconnecting" while an ESTABLISHED live
   * session's WebSocket is down and the manager is retrying (DATA-02 —
   * `mode` stays "live": the data on screen is real, just frozen); "mock"
   * otherwise; "demo" when live mode connected + authed fine but found no
   * BESS-site under the bound entity, so we dropped into the simulator
   * (this powers a ThingsBoard demo page). "error" for a genuine
   * auth/network failure. Independent from `mode` so the UI can
   * distinguish "user asked for live but TB hasn't answered yet" from
   * "user asked for mock".
   */
  connection: "mock" | "connecting" | "live" | "reconnecting" | "demo" | "error";
  connectionError?: string;
  /**
   * Timestamp (ms) of the most recent live-connection failure, or null.
   * Cleared on every successful live connect. Drives the persistent
   * "SIMULATED DATA" banner shown when the operator enters mock AFTER a
   * live failure ("Use mock data" recovery click) — persisted in
   * sessionStorage because that click performs a full ?mock=1 page
   * navigation that would otherwise wipe the flag.
   */
  liveErrorAt: number | null;
  /**
   * Sites the operator can swap to. Empty in mock mode (the mock site
   * picker uses {@link TOPOLOGIES} directly). In live mode this is the
   * adapter's `availableSites` — sites under the bound grouping asset,
   * or all discovered Installations.
   */
  availableSites: ReadonlyArray<AvailableSite>;
  /** `opts.restore` marks a boot-time last-site restore (DATA-07): on ANY
   *  failure the swap degrades softly — stale key dropped, default site
   *  kept, one warn toast — instead of the manual-click error/demo paths. */
  setSite(id: string, opts?: { restore?: boolean }): void;
  setMode(m: AdapterMode): void;
  setOperator(name: string): void;
}

/**
 * The store's 6-value connection union, exported so pure presentation
 * selectors (e.g. lib/selectors/freshnessBucket) can type their inputs
 * without re-declaring the union. Derived from the interface so the two
 * can never drift.
 */
export type Connection = SiteStoreState["connection"];

/**
 * PERS-10 — sticky boot params. The env params that select and wire the
 * data source (live/mock, TB endpoints, auth token, theme, host origin,
 * debug) arrive only on the FIRST URL of a session: App.tsx's
 * RootRedirect/ModeRouter replace-redirects (and any link that bypasses
 * lib/carryParams) strip them from the address bar, so a later F5 would
 * silently boot a standalone live session into mock. This module runs at
 * import time, BEFORE any router redirect, so we stash whatever boot
 * params are present into sessionStorage and let detectMode() /
 * tryBuildTbAdapter() fall back to the stash when the URL no longer
 * carries them. Per-tab scope is deliberate: a NEW tab opened on a bare
 * URL must boot with its own defaults, not a previous session's JWT.
 */
const BOOT_PARAMS_KEY = "ems-boot-params";
const BOOT_PARAM_KEYS = ["live", "mock", "tb", "tbws", "token", "theme", "hostOrigin", "debug", "lang"] as const;

function stashBootParams(): void {
  if (typeof window === "undefined") return;
  try {
    const sp = new URL(window.location.href).searchParams;
    const present: Record<string, string> = {};
    for (const k of BOOT_PARAM_KEYS) {
      const v = sp.get(k);
      if (v !== null) present[k] = v;
    }
    // No boot params in the URL → keep any existing stash. This is
    // exactly the post-redirect reload the stash exists for.
    if (Object.keys(present).length === 0) return;
    // MERGE over the existing stash, never replace wholesale — the ?mock=1
    // recovery navigation carries ONLY that one param, and wiping the
    // stashed token/tb endpoints would leave the Retry-live button unable
    // to ever reconnect. mock/live stay mutually exclusive: whichever is
    // present in THIS URL wins (a deliberate mock click beats a stashed
    // live, and vice versa).
    const merged = { ...readBootParamStash(), ...present };
    if ("mock" in present) delete merged.live;
    if ("live" in present) delete merged.mock;
    sessionStorage.setItem(BOOT_PARAMS_KEY, JSON.stringify(merged));
  } catch { /* sandboxed storage — params just won't survive a reload */ }
}

function readBootParamStash(): Record<string, string> {
  try {
    const raw = sessionStorage.getItem(BOOT_PARAMS_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : null;
    if (!parsed || typeof parsed !== "object") return {};
    const out: Record<string, string> = {};
    for (const [k, v] of Object.entries(parsed)) {
      if (typeof v === "string") out[k] = v;
    }
    return out;
  } catch { return {}; }
}

/** Read a boot param: the live URL always wins; stash is the fallback. */
function bootParam(sp: URLSearchParams, key: string): string | null {
  return sp.get(key) ?? readBootParamStash()[key] ?? null;
}

stashBootParams();

/**
 * Detect run-mode from URL params and iframe context.
 *
 *   mode      trigger                                       data source
 *   -----     ------------------------------------          --------------
 *   mock      default                                       MockSite (in-browser synthetic)
 *   live      `?live=1` or `window.parent !== window`       TbAdapter (real ThingsBoard)
 *
 * When `live` is selected but the adapter fails to construct (e.g. host
 * widget hasn't sent ctx yet, or REST returns 401), we fall back to mock
 * with a warning so the SPA stays usable.
 */
function detectMode(): AdapterMode {
  if (typeof window === "undefined") return "mock";
  const u = new URL(window.location.href);
  // Explicit URL params first — BOTH checked before the stash so a
  // deliberate ?mock=1 visit can never be vetoed by a stashed live=1.
  if (u.searchParams.get("live") === "1") return "live";
  if (u.searchParams.get("mock") === "1") return "mock";
  // Stashed boot params next (PERS-10): a standalone live session
  // survives navigate + reload even after a redirect dropped the params.
  const stash = readBootParamStash();
  if (stash.live === "1") return "live";
  if (stash.mock === "1") return "mock";
  // Iframed → presume embedded TB host widget is the data source.
  if (window.parent !== window) return "live";
  return "mock";
}

/**
 * DATA-07 / PERS-07 — the last chosen site survives reloads.
 *
 * Mock mode: plain `ems-last-site` key, validated against TOPOLOGIES at
 * boot (topologyById throws on unknown ids, so never trust the raw value).
 *
 * Live mode: keyed per bound context — `ems-last-site:<ctxKey>` — so two
 * TB dashboards bound to different grouping assets can never hijack each
 * other's choice. The adapter does not retain the grouping asset's own id
 * once it has resolved to an Installation, so the context key is the first
 * entry of the name-sorted `availableSites` list: stable across swaps
 * (the store preserves the sibling list) and across reloads (same grouping
 * → same site set → same sorted-first id). Single-site contexts have
 * nothing to restore and get no key.
 */
const LAST_SITE_KEY = "ems-last-site";

function readLastMockSite(): string | null {
  try {
    const id = localStorage.getItem(LAST_SITE_KEY);
    return id && TOPOLOGIES.some((t) => t.id === id) ? id : null;
  } catch { return null; }
}

function writeLastMockSite(id: string): void {
  try { localStorage.setItem(LAST_SITE_KEY, id); } catch { /* ignore */ }
}

/** Stable per-bound-context storage key, or null when there is ≤ 1 site. */
function liveLastSiteKey(sites: ReadonlyArray<AvailableSite>): string | null {
  return sites.length > 1 ? `${LAST_SITE_KEY}:${sites[0].id}` : null;
}

function readLastLiveSite(sites: ReadonlyArray<AvailableSite>): string | null {
  const key = liveLastSiteKey(sites);
  if (!key) return null;
  try {
    const id = localStorage.getItem(key);
    return id && sites.some((s) => s.id === id) ? id : null;
  } catch { return null; }
}

function writeLastLiveSite(sites: ReadonlyArray<AvailableSite>, id: string): void {
  const key = liveLastSiteKey(sites);
  if (!key) return;
  try { localStorage.setItem(key, id); } catch { /* ignore */ }
}

/**
 * PERS-07 failure path: drop the persisted last-site keys (plain mock key
 * + the per-context live variant) so a stale value can never wedge every
 * subsequent boot in a failed-restore loop.
 */
function clearLastSiteKeys(sites: ReadonlyArray<AvailableSite>): void {
  try { localStorage.removeItem(LAST_SITE_KEY); } catch { /* ignore */ }
  const key = liveLastSiteKey(sites);
  if (key) { try { localStorage.removeItem(key); } catch { /* ignore */ } }
}

const initialMode: AdapterMode = detectMode();
const initialId = readLastMockSite() ?? TOPOLOGIES[0].id;

/**
 * sessionStorage-backed persistence for {@link SiteStoreState.liveErrorAt}.
 * The "Use mock data" recovery action navigates to ?mock=1 (a full page
 * load), so an in-memory flag alone would be lost exactly when the
 * SIMULATED-DATA banner needs to appear. Per-tab scope is right: a fresh
 * tab opened deliberately on ?mock=1 should NOT carry the failure banner.
 */
const LIVE_ERROR_AT_KEY = "ems-live-error-at";

function readLiveErrorAt(): number | null {
  try {
    const raw = sessionStorage.getItem(LIVE_ERROR_AT_KEY);
    const ts = raw === null ? NaN : Number(raw);
    return Number.isFinite(ts) ? ts : null;
  } catch { return null; }
}

/** Record a live failure (returns the timestamp to put in the store). */
function recordLiveError(): number {
  const ts = Date.now();
  try { sessionStorage.setItem(LIVE_ERROR_AT_KEY, String(ts)); } catch { /* ignore */ }
  return ts;
}

/** Clear the failure marker (returns null to put in the store). */
function clearLiveError(): null {
  try { sessionStorage.removeItem(LIVE_ERROR_AT_KEY); } catch { /* ignore */ }
  return null;
}

function buildMockAdapter(topologyId: string): EmsAdapter {
  return new MockAdapter(topologyById(topologyId));
}

/** Placeholder identity until the operator sets a name (or TB supplies one). */
const DEFAULT_OPERATOR = "operator";

function readOperatorName(): string {
  // try/catch: localStorage can throw inside a sandboxed iframe.
  try { return localStorage.getItem("ems-op-name") || DEFAULT_OPERATOR; } catch { return DEFAULT_OPERATOR; }
}

/**
 * DATA-10 — default the operator identity from the authenticated TB user
 * so the alarm audit trail stops reading "operator". Only runs while the
 * store still holds the hardcoded default: an explicitly-set name
 * (persisted by setOperator → localStorage ems-op-name) is never
 * overwritten. The TB-derived name is deliberately NOT persisted — a
 * different TB login on the same browser must not inherit it.
 */
function adoptTbOperatorName(tb: TbAdapter): void {
  if (useSiteStore.getState().operator !== DEFAULT_OPERATOR) return;
  tb.currentUserName()
    .then((name) => {
      if (!name) return;
      if (useSiteStore.getState().operator !== DEFAULT_OPERATOR) return;
      useSiteStore.setState({ operator: name });
    })
    .catch(() => { /* keep the default — endpoint unreachable */ });
}

/**
 * Try to construct a TbAdapter. Returns null on failure so caller falls back.
 *
 * REST + WS base URLs are resolved in this order:
 *   1. `?tb=…` / `?tbws=…` URL params (manual override for dev).
 *   2. `VITE_TB_WS_BASE` build-time env var (set in vercel.json) — used
 *      as the WS base when REST is proxied through Vercel rewrites.
 *   3. Empty string (same-origin) — the production-embedded default.
 *
 * Embedded mode (iframed) gets its auth JWT via the postMessage bridge.
 * Direct dev mode reads `?token=` from the URL.
 */
async function tryBuildTbAdapter(override?: { entityOverride?: EntityRef; embedded?: EmbeddedCtxSource }): Promise<TbAdapter | null> {
  if (typeof window === "undefined") return null;
  // Each param falls back to the sessionStorage boot stash (PERS-10) when
  // absent from the current URL — a redirect/navigation may have stripped
  // it after boot.
  const sp = new URL(window.location.href).searchParams;
  const restBase = bootParam(sp, "tb") ?? "";
  // WS goes direct to TB even when REST is proxied (Vercel can't tunnel
  // WebSockets to an arbitrary upstream). VITE_TB_WS_BASE is baked into
  // the bundle at build time by vercel.json's build.env.
  const wsBase =
    bootParam(sp, "tbws")
    ?? (typeof import.meta !== "undefined" ? (import.meta as ImportMeta & { env?: Record<string, string> }).env?.VITE_TB_WS_BASE : undefined)
    ?? restBase
    ?? "";
  const jwt = bootParam(sp, "token") ?? undefined;
  const embedded = override?.embedded
    ?? (EmbeddedCtxSource.isEmbedded()
        ? new EmbeddedCtxSource(bootParam(sp, "hostOrigin") ?? "*")
        : undefined);
  // Embedded mode: wait briefly for the host to deliver auth.token before
  // attempting discovery. The host widget fires ctx.init + auth.token in
  // response to our `ready` post.
  // Embedded mode: wait for BOTH `auth.token` AND `ctx.init` (with its
  // optional `entity` field) before discovering. The entity hint is what
  // lets each widget instance scope to its own site — without it, the
  // adapter falls back to "first discovered site" and the user only sees
  // whichever asset TB returns first (the "Basiano-only" bug under tenant
  // admin).
  if (embedded) {
    await new Promise<void>((resolve) => {
      let gotAuth = !!embedded.getAuthToken();
      let gotCtx = embedded.isReady();
      const maybeResolve = () => {
        if (gotAuth && gotCtx) { dAuth(); dCtx(); resolve(); }
      };
      const dAuth = embedded.onAuthChange((t) => { if (t) { gotAuth = true; maybeResolve(); } });
      const dCtx  = embedded.onCtxChange(()    => { gotCtx = true; maybeResolve(); });
      maybeResolve();
      // Hard cap: 4 s. If only auth arrived (no ctx.init yet), proceed
      // anyway — discovery will fall back to first-site.
      window.setTimeout(() => { dAuth(); dCtx(); resolve(); }, 4000);
    });
    if (!embedded.getAuthToken()) {
      console.warn("[operator-spa] embedded mode but no auth.token received within 4 s — falling back to mock.");
      return null;
    }
  } else if (!jwt) {
    console.warn("[operator-spa] live mode requires ?token=<jwt> in direct dev mode.");
    return null;
  }
  // Read the entity hint from the bridge (when present). The bridge type
  // uses string entity types; the TB EntityRef type used by tbAdapter is
  // narrower — only ASSET/DEVICE/etc. are real targets. We cast through
  // unknown so the runtime check (in TbAdapter.fromTenant) is the
  // gatekeeper.
  //
  // `entityOverride` wins when present (live-mode site swap from the
  // SiteSwitcher — points at a specific Installation inside a grouping
  // asset, bypassing the grouping-walk).
  let entity: EntityRef | null = override?.entityOverride ?? null;
  if (!entity) {
    const ctxEntity = embedded?.getCtx().entity ?? null;
    entity = ctxEntity && typeof ctxEntity.type === "string" && typeof ctxEntity.id === "string"
      ? { type: ctxEntity.type as EntityRef["type"], id: ctxEntity.id }
      : null;
  }
  try {
    return await TbAdapter.fromTenant({
      restBaseUrl: restBase,
      wsBaseUrl: wsBase,
      jwt,
      embedded,
      entity,
    });
  } catch (err) {
    // A "no BESS-site under the bound entity" result is NOT a failure — let it
    // propagate so the caller drops into the mock simulator (demo page). Only
    // genuine auth/network errors collapse to null (→ "Live data unavailable").
    if (err instanceof NoSiteAssetsError) throw err;
    console.warn("[operator-spa] failed to build TbAdapter:", err);
    return null;
  }
}

const initialAdapter = buildMockAdapter(initialId);
const initialSnap = initialAdapter.latest();

function seedRecent(adapter: EmsAdapter, latest: SiteSnapshot): SiteSnapshot[] {
  const anyAdapter = adapter as { recent?: () => SiteSnapshot[] };
  if (typeof anyAdapter.recent === "function") return anyAdapter.recent();
  return [latest];
}
const initialRecent = seedRecent(initialAdapter, initialSnap);

let activeDispose: (() => void) | null = null;

export const useSiteStore = create<SiteStoreState>((set, get) => ({
  mode: initialMode,
  topologyId: initialId,
  adapter: initialAdapter,
  snapshot: initialSnap,
  recent: initialRecent,
  operator: readOperatorName(),
  connection: initialMode === "live" ? "connecting" : "mock",
  liveErrorAt: readLiveErrorAt(),
  availableSites: [],
  setSite: (id, opts) => {
    const state = get();
    if (state.mode === "live") {
      // Live mode: swap to a sibling Installation (same TB session,
      // same auth) by rebuilding the adapter with an entity override.
      const target = state.availableSites.find((s) => s.id === id);
      if (!target) {
        console.warn("[operator-spa] setSite: id not in availableSites:", id);
        pushToast({ level: "critical", title: "Unknown site — switch failed" });
        return;
      }
      if (target.id === state.adapter.topology().id) return; // already active

      // CRITICAL: preserve the original `availableSites` across the swap.
      // The new adapter is built via `entityOverride` pointing directly
      // at a single Installation, so `fromTenant` resolves through the
      // "Installation → use directly" branch and reports just one site
      // in its own `availableSites`. Without this preservation the
      // operator would be stranded on the new site with no way back.
      const preservedSiblings = state.availableSites;

      // A RESTORE-invoked swap (boot-time last-site replay) must never
      // wedge boot: the previous adapter is only torn down on swap
      // SUCCESS, so on failure the already-connected default site is
      // still live underneath — drop the stale key, surface one warn
      // toast, and stay there. No enterDemo, no error splash.
      const isRestore = opts?.restore === true;
      const failRestore = () => {
        clearLastSiteKeys(preservedSiblings);
        set({ connection: "live", connectionError: undefined });
        pushToast({ level: "warn", title: "Couldn't restore last site — staying on the default site" });
      };

      set({ connection: "connecting", connectionError: undefined });
      tryBuildTbAdapter({ entityOverride: target.ref })
        .then((tb) => {
          if (!tb) {
            if (isRestore) { failRestore(); return; }
            set({ connection: "error", connectionError: "Could not swap to selected site — TB rejected the request.", liveErrorAt: recordLiveError() });
            return;
          }
          activeDispose?.();
          // Tear down the previous adapter so its WebSocket/HTTP client
          // releases the JWT and the snapshot assembler stops fetching.
          // Without this, the old TB session can keep streaming for up
          // to 30 s after the swap (paho-mqtt keep-alive).
          try { state.adapter.destroy?.(); } catch (e) {
            console.warn("[operator-spa] previous adapter destroy() threw:", e);
          }
          const snap = tb.latest();
          activeDispose = wireLive(tb);
          applyBackfill(tb);
          set({
            adapter: tb,
            snapshot: snap,
            recent: isHydrated(snap) ? [freezeSnapshot(snap)] : [],
            topologyId: tb.site.site.id,
            // Keep the parent grouping's full sibling list, NOT just the
            // newly-built adapter's one-entry availableSites.
            availableSites: preservedSiblings,
            connection: "live",
            connectionError: undefined,
            liveErrorAt: clearLiveError(),
          });
          // DATA-07: remember the swap per bound context so the next boot
          // under this grouping restores it.
          writeLastLiveSite(preservedSiblings, tb.site.site.id);
          adoptTbOperatorName(tb);
        })
        .catch((err) => {
          if (isRestore) {
            console.warn("[operator-spa] last-site restore failed:", err);
            failRestore();
            return;
          }
          if (err instanceof NoSiteAssetsError) { enterDemo(err.message); return; }
          console.error("[operator-spa] live site swap crashed:", err);
          set({ connection: "error", connectionError: String(err?.message ?? err), liveErrorAt: recordLiveError() });
        });
      return;
    }
    const adapter = buildMockAdapter(id);
    const snap = adapter.latest();
    activeDispose?.();
    try { state.adapter.destroy?.(); } catch (e) {
      console.warn("[operator-spa] previous adapter destroy() threw:", e);
    }
    activeDispose = wireSnapshots(adapter);
    set({ topologyId: id, adapter, snapshot: snap, recent: seedRecent(adapter, snap) });
    // DATA-07: only reached after buildMockAdapter validated the id
    // (topologyById throws on unknown ids).
    writeLastMockSite(id);
  },
  setMode: (m) => {
    const prev = get().adapter;
    if (m === "mock") {
      activeDispose?.();
      try { prev.destroy?.(); } catch (e) {
        console.warn("[operator-spa] previous adapter destroy() threw:", e);
      }
      const adapter = buildMockAdapter(get().topologyId);
      const snap = adapter.latest();
      activeDispose = wireSnapshots(adapter);
      set({ mode: m, adapter, snapshot: snap, recent: seedRecent(adapter, snap), availableSites: [], connection: "mock", connectionError: undefined });
    } else {
      // Async swap to TB. Keep the mock adapter wired meanwhile so the
      // ConnectingSplash has something to render under, but reset
      // recent[] to a single live snap when TB delivers — we never want
      // mock data leaking into a live spark chart.
      set({ mode: m, connection: "connecting", connectionError: undefined });
      tryBuildTbAdapter().then((tb) => {
        if (!tb) {
          // Keep mode "live" so the ConnectingSplash error card stays
          // mounted — entering mock requires an explicit operator click.
          set({ connection: "error", connectionError: "TB adapter could not be constructed.", liveErrorAt: recordLiveError() });
          return;
        }
        activeDispose?.();
        try { prev.destroy?.(); } catch (e) {
          console.warn("[operator-spa] previous adapter destroy() threw:", e);
        }
        const snap = tb.latest();
        activeDispose = wireLive(tb);
        applyBackfill(tb);
        set({
          adapter: tb,
          snapshot: snap,
          recent: isHydrated(snap) ? [freezeSnapshot(snap)] : [],
          topologyId: tb.site.site.id,
          availableSites: tb.availableSites,
          connection: "live",
          connectionError: undefined,
          liveErrorAt: clearLiveError(),
        });
        adoptTbOperatorName(tb);
        // DATA-07: restore the operator's last sibling-site choice for
        // this bound context (no-op when none stored / single site).
        const lastSite = readLastLiveSite(tb.availableSites);
        if (lastSite && lastSite !== tb.site.site.id) get().setSite(lastSite, { restore: true });
      }).catch((err) => {
        if (err instanceof NoSiteAssetsError) { enterDemo(err.message); return; }
        console.error("[operator-spa] live swap crashed:", err);
        set({ connection: "error", connectionError: String(err?.message ?? err), liveErrorAt: recordLiveError() });
      });
    }
  },
  setOperator: (name) => {
    try { localStorage.setItem("ems-op-name", name); } catch { /* ignore */ }
    set({ operator: name });
  },
}));

/**
 * Take a stable, frozen copy of a snapshot for the recent[] buffer.
 *
 * The live TB assembler mutates a single SiteSnapshot in place and emits
 * the same reference every flush. If we pushed that reference into
 * recent[], every entry would alias the LATEST values — which is exactly
 * what produced the "flat chart with tooltip showing the same value six
 * times" bug. We need each entry to capture its own values at its own ts.
 *
 * structuredClone is available on every browser we support (Chrome 98+,
 * Firefox 94+, Safari 15.4+). Fall back to JSON if absent.
 */
function freezeSnapshot(s: SiteSnapshot): SiteSnapshot {
  if (typeof structuredClone === "function") return structuredClone(s) as SiteSnapshot;
  return JSON.parse(JSON.stringify(s)) as SiteSnapshot;
}

/**
 * A snapshot is "hydrated" once it carries telemetry from at least one
 * BESS unit. The TB SnapshotAssembler emits an INITIAL all-zeros
 * snapshot (online=false, soc=0, active_W=0 for every unit) at adapter
 * construction and then again synchronously from `onSnapshot()` when
 * `subscribe()` is wired — BEFORE any WS message has landed.
 *
 * Pushing those pre-telemetry snapshots into recent[] creates a phantom
 * "spike to zero" at the rightmost edge of every chart that consumes
 * recent[] (the BessSocPowerChart, PowerSparkChart, etc.). Even the
 * historical backfill can't hide it — backfill snapshots land in the
 * past while the zero snap sits at swap-time. Once a real WS message
 * arrives, the snapshot mutates to real values; until then it's noise.
 *
 * Filter behaviour:
 *   - Mock snapshots have `online: true` from t=0 → always hydrated.
 *   - Live snapshots gain `online: true` per-unit as WS samples arrive.
 *   - A snapshot with NO online unit is rejected.
 */
function isHydrated(s: SiteSnapshot): boolean {
  return s.bess_per_unit.some((u) => u.online);
}

function wireSnapshots(adapter: EmsAdapter): () => void {
  return adapter.subscribe((snap) => {
    if (!isHydrated(snap)) {
      // The store's `snapshot` field can still reflect the live state
      // for "right now" panels (they show "—" when fields are zero),
      // but recent[] stays untouched so historical charts don't
      // inherit the artifact.
      useSiteStore.setState({ snapshot: snap });
      return;
    }
    useSiteStore.setState((state) => ({
      snapshot: snap,
      recent: pruneRecent(state.recent, freezeSnapshot(snap)),
    }));
  });
}

activeDispose = wireSnapshots(initialAdapter);

/**
 * ITEM 4.1b / DATA-02 — live-link state machine.
 *
 * Wall-clock of the most recent "Live link lost" toast. Used to suppress
 * the "restored" toast when the link flaps back within 10 s — the operator
 * shouldn't get a two-toast volley for a blip the pill already narrated.
 * Module-level on purpose: it must survive an adapter swap mid-outage.
 */
let lastLinkDropToastAt = 0;

/**
 * Structural view of the link hooks TbAdapter is gaining (DATA-02,
 * cluster A). Optional-chained so this store works before AND after those
 * land — until then the callbacks are simply never registered and the
 * connection stays "live" between explicit swaps, exactly as today. The
 * MockAdapter never gets wired here (mock has no link to lose).
 */
type LiveLinkHooks = {
  onStateChange?: (cb: (state: string) => void) => (() => void) | void;
  onAuthExpired?: (cb: () => void) => (() => void) | void;
  /** Embedded only: fires when the host posts a CHANGED auth token. */
  onAuthTokenChange?: (cb: (token: string | null) => void) => (() => void) | void;
};

/**
 * Map WS-manager state onto the store's connection field:
 *   socket down ("reconnecting"/"closed") while connection is "live"
 *     → "reconnecting" (mode untouched — the data shown is real, frozen)
 *   socket "open" while connection is "reconnecting"
 *     → back to "live"
 * "connecting" is transitional (a retry in flight) and is ignored, so the
 * pill doesn't flap during an outage's retry loop. Auth expiry sets
 * "error" + the standing recordLiveError marker, but is RECOVERABLE while
 * this adapter stays wired: a fresh embedded token flips it back to "live".
 */
function wireLiveLink(tb: TbAdapter): () => void {
  const hooks = tb as TbAdapter & LiveLinkHooks;
  const disposers: Array<() => void> = [];
  // A disposed adapter's late events must never mutate state for its successor.
  const isCurrent = () => useSiteStore.getState().adapter === tb;

  const dState = hooks.onStateChange?.((state) => {
    if (!isCurrent()) return;
    const conn = useSiteStore.getState().connection;
    if (state === "open") {
      if (conn !== "reconnecting") return;
      useSiteStore.setState({ connection: "live", connectionError: undefined });
      // Suppress the recovery toast within 10 s of the drop toast — a
      // flapping link should not narrate every bounce.
      if (Date.now() - lastLinkDropToastAt > 10_000) {
        pushToast({ level: "ok", title: "Live link restored" });
      }
    } else if (state === "reconnecting" || state === "closed") {
      if (conn !== "live") return; // never stomp connecting/error/demo
      useSiteStore.setState({ connection: "reconnecting" });
      lastLinkDropToastAt = Date.now();
      pushToast({ level: "warn", title: "Live link lost — reconnecting…" });
    }
  });
  if (typeof dState === "function") disposers.push(dState);

  const dAuth = hooks.onAuthExpired?.(() => {
    if (!isCurrent()) return;
    if (useSiteStore.getState().connection === "error") return;
    useSiteStore.setState({
      connection: "error",
      connectionError: "Session expired — reload the dashboard",
      liveErrorAt: recordLiveError(),
    });
  });
  if (typeof dAuth === "function") disposers.push(dAuth);

  // Belt + braces for auth expiry (DOCS-03): the host widget's 60 s JWT
  // poll usually delivers a fresh token shortly after `auth.refreshNeeded`.
  // If it lands while this adapter is still wired, recover the terminal
  // "error" back to "live" — the http/ws layers read the token lazily, so
  // the session is genuinely usable again.
  const dToken = hooks.onAuthTokenChange?.((token) => {
    if (!token || !isCurrent()) return;
    if (useSiteStore.getState().connection !== "error") return;
    useSiteStore.setState({
      connection: "live",
      connectionError: undefined,
      liveErrorAt: clearLiveError(),
    });
  });
  if (typeof dToken === "function") disposers.push(dToken);

  return () => {
    for (const d of disposers) {
      try { d(); } catch { /* already torn down */ }
    }
  };
}

/** Wire a live adapter: snapshot stream + link state machine (DATA-02). */
function wireLive(tb: TbAdapter): () => void {
  const disposeSnapshots = wireSnapshots(tb);
  const disposeLink = wireLiveLink(tb);
  return () => { disposeSnapshots(); disposeLink(); };
}

/**
 * Pull `adapter.recentHistory()` and union it into the store's recent[]
 * buffer. Used by every live-mode entry path so the operator always
 * sees the genuine last 60 minutes immediately on page load, not just
 * the snapshots that have arrived since the page opened.
 *
 * Safe to call after the live adapter is wired — if the backfill
 * returns empty (no REST permission, no historical data), it's a no-op.
 */
function applyBackfill(adapter: EmsAdapter) {
  adapter.recentHistory().then((backfill) => {
    if (!backfill.length) return;
    useSiteStore.setState((state) => {
      // If the adapter was swapped while we were awaiting, ignore.
      if (state.adapter !== adapter) return state;
      return { recent: mergeBackfill(state.recent, backfill) };
    });
  }).catch(() => {
    // Non-fatal — live stream still fills the buffer over time.
  });
}

/**
 * Drop into the in-browser SIMULATOR ("demo") — used when live mode connected
 * and authed fine but found no BESS-site under the bound entity alias
 * ({@link NoSiteAssetsError}). This is what lets a ThingsBoard dashboard embed
 * the console as a demo page with any/no entity bound: instead of an error, the
 * operator sees fully-working synthetic data they can click through.
 *
 * Behaves exactly like mock mode (`mode: "mock"` → the SiteSwitcher uses
 * {@link TOPOLOGIES}, the ConnectingSplash hides), but `connection: "demo"` lets
 * the chrome label it "Demo" rather than "Mock" so it's clear it's a simulator
 * standing in for an unbound widget.
 */
function enterDemo(reason: string) {
  const prev = useSiteStore.getState().adapter;
  activeDispose?.();
  try { prev.destroy?.(); } catch (e) {
    console.warn("[operator-spa] previous adapter destroy() threw:", e);
  }
  const adapter = buildMockAdapter(initialId);
  const snap = adapter.latest();
  activeDispose = wireSnapshots(adapter);
  useSiteStore.setState({
    mode: "mock",
    topologyId: initialId,
    adapter,
    snapshot: snap,
    recent: seedRecent(adapter, snap),
    availableSites: [],
    connection: "demo",
    connectionError: reason,
    // Demo is NOT a failure (TB connected + authed fine) — don't leave a
    // stale failure marker that would pin the SIMULATED-DATA banner.
    liveErrorAt: clearLiveError(),
  });
}

// If we detected live mode at boot, kick off TB discovery immediately.
// The ConnectingSplash overlay (mounted in App.tsx) hides the mock
// underneath while `connection === "connecting"`, so the operator never
// sees mock data flicker into view in live mode. When TB delivers the
// splash unmounts and the user sees real data from t=0; on failure the
// splash stays up with the error card (mode stays "live" so the mock
// adapter underneath never renders uninvited — entering mock requires
// the explicit "Use mock data" click).
if (initialMode === "live") {
  void (async () => {
    try {
      const tb = await tryBuildTbAdapter();
      if (!tb) {
        useSiteStore.setState({
          connection: "error",
          connectionError: "TB host did not deliver auth.token within 4 s, or REST discovery failed.",
          liveErrorAt: recordLiveError(),
        });
        return;
      }
      activeDispose?.();
      const snap = tb.latest();
      activeDispose = wireLive(tb);
      // Kick off the historical 60-min backfill in the background so
      // the spark chart populates with real history immediately rather
      // than waiting 60 min for live uplinks to fill the buffer.
      applyBackfill(tb);
      useSiteStore.setState({
        adapter: tb,
        snapshot: snap,
        recent: isHydrated(snap) ? [freezeSnapshot(snap)] : [],
        topologyId: tb.site.site.id,
        availableSites: tb.availableSites,
        connection: "live",
        connectionError: undefined,
        liveErrorAt: clearLiveError(),
      });
      adoptTbOperatorName(tb);
      // DATA-07: restore the last-chosen sibling site for this bound
      // context. Reuses the setSite swap path (entityOverride rebuild) so
      // teardown + sibling-list preservation behave exactly like a manual
      // SiteSwitcher click.
      const lastSite = readLastLiveSite(tb.availableSites);
      if (lastSite && lastSite !== tb.site.site.id) {
        useSiteStore.getState().setSite(lastSite, { restore: true });
      }
    } catch (err) {
      if (err instanceof NoSiteAssetsError) {
        enterDemo(err.message);
        return;
      }
      console.error("[operator-spa] boot-time live swap crashed:", err);
      useSiteStore.setState({
        connection: "error",
        connectionError: String((err as Error)?.message ?? err),
        liveErrorAt: recordLiveError(),
      });
    }
  })();
}

export const useTopology = () => useSiteStore((s) => s.adapter.topology());
export const useSnapshot = () => useSiteStore((s) => s.snapshot);
export const useAdapter = () => useSiteStore((s) => s.adapter);
export const useRecent = () => useSiteStore((s) => s.recent);
export const useAvailableSites = () => useSiteStore((s) => s.availableSites);

/**
 * Connection-state selector. Wrapped in `useShallow` because Zustand v5
 * uses `useSyncExternalStore` under the hood — a selector returning a
 * fresh object on every call would be detected as "changed" every tick
 * and React would throw error #185 (Maximum update depth exceeded).
 */
export const useConnection = () =>
  useSiteStore(useShallow((s) => ({ mode: s.mode, connection: s.connection, error: s.connectionError })));
